import { getEnvVar } from 'src/lib/env'

const BASE_TABS = {
  ALL_PLANS: 'ALL_PLANS',
  FAVORITES: 'FAVORITES',
  SHARED_WITH_ME: 'SHARED_WITH_ME',
}

export const PLANS_TABS = {
  ...BASE_TABS,
  DIVISION: 'DIVISION',
  DRAFTS: 'DRAFTS',
}

export const DIVISION_VIEW_PLAN_TABS = {
  ...BASE_TABS,
  DIVISION: 'DIVISION',
}

export const CORPORATE_VIEW_PLAN_TABS = {
  ...BASE_TABS,
}

export const CORPORATE_CREATE_PLAN_TABS = {
  ...BASE_TABS,
  DRAFTS: 'DRAFTS',
}

export const MULTI_FAMILY_TABS = {
  ALL_PLANS: 'ALL_PLANS',
  DIVISION: 'DIVISION',
  DRAFTS: 'DRAFTS',
}

export const VIEW_ONLY_MULTI_FAMILY_TABS = {
  SHELL_PLANS: 'SHELL_PLANS',
  UNIT_PLANS: 'UNIT_PLANS',
}

export const VIEW_ONLY_SINGLE_FAMILY_TABS = {
  ALL_PLANS: 'ALL_PLANS',
}

export const VIEW_ONLY_FAVORITES_TABS = {
  SINGLE_FAMILY_PLANS: 'SINGLE_FAMILY_PLANS',
  ...VIEW_ONLY_MULTI_FAMILY_TABS,
}

export const SIDE_BAR_TABS = {
  PENDING_PLANS: 'PENDING_PLANS',
}

export const ADDITIONAL_FEATURES_SECTION_NAME = 'Additional Features'

export const JDE_DISPLAY_ONLY_MESSAGE =
  'Display only, pricing managed in JDE E1'

export const PLAN_REGIONS = [
  'Florida',
  'Grove',
  'Parker',
  'Taylor',
  'West',
] as const

/**
 * Approved Regional Plans are partitioned by BUSINESS region, not PLR region:
 * LTGC-5709 writes only a business region on them, and the grid projection uses it
 * as the partition key's region component. The all-community-plans fan-out targets
 * partitions by (region, planCategory, hierarchy), so this category has to be
 * enumerated over these values instead of PLAN_REGIONS. Only Parker, Grove and
 * Taylor appear in both lists, so using PLAN_REGIONS here reaches just those three
 * and silently loses every plan in the other six business regions.
 *
 * Mirrors BusinessRegionEnum in plan-library-common, which the FE's pinned 1.2.10
 * does not export, PLUS 'Florida'. Florida is not a BusinessRegionEnum member — it
 * is a PLR region — but the Florida workbook's plans are approved as Florida
 * regardless of the division selling them, so the port writes that as their business
 * region. Omitting it here would leave those 133 plans in a partition the fan-out
 * never asks for and invisible in the grid, with no error to show why.
 *
 * Kept as a static list because the fan-out runs before the divisionsAndRegions API
 * response is guaranteed to be in the store, and because it must stay in step with
 * what the port writes rather than with what that endpoint returns.
 *
 * The October 2026 realignment (LTGC-5474) renamed the Parker business region to
 * Martin and created Nason (Mid-Florida, Orlando, Tampa); the LTGC-5474 data
 * migration renames the existing ARP documents, so 'Parker' would be an empty
 * partition and Martin/Nason plans would be invisible without this list matching.
 */
export const PLAN_BUSINESS_REGIONS = [
  'Florida',
  'Gonzalez',
  'McMurray',
  'Martin',
  'Mignone',
  'Jensen',
  'Burgess',
  'Grove',
  'Nason',
  'Taylor',
  'Parness',
  'Walker',
] as const

/**
 * The PLAN_BUSINESS_REGIONS values that are not business regions, mapped to the
 * business regions whose divisions they cover, so the Division filter can offer
 * the right divisions for them.
 *
 * The Florida workbook's plans are approved as 'Florida' whichever division sells
 * them, and its rows span every Florida division: Gonzalez's, McMurray's and, since
 * the LTGC-5474 realignment, Nason's. The PLR Florida region is no substitute — that
 * realignment moved Mid-Florida, Orlando and Tampa out of it into Grove.
 */
export const APPROVED_REGIONAL_PLAN_REGION_BUSINESS_REGIONS: Readonly<
  Record<string, readonly string[]>
> = {
  Florida: ['Gonzalez', 'McMurray', 'Nason'],
}

export const PLAN_CATEGORY_QUERY_VALUES = [
  'Community',
  'Core',
  'High Value Plan',
  'Approved Regional Plan',
] as const

export const APPROVED_REGIONAL_PLAN_QUERY_CATEGORY = 'Approved Regional Plan'

export const HIERARCHY_QUERY_VALUES = [
  'Parent',
  'Child',
  'Grandchild',
  '',
] as const

export const VALUE_TWO = 2
export const VALUE_SEVEN = 7
export const DECIMAL_NO_REMAINDER = 0
export const COMPARISON_FEATURE_THRESHOLD_PLAN_COUNT = 20
export const PROPAGATE_FEATURE_THRESHOLD_PLAN_COUNT = 100
export const MARK_AS_CORE_THRESHOLD_PLAN_COUNT = 100
export const LAST_6_DIGITS_TO_TRIM_FROM_COMMUNITY_ID = -6
export const DIVISION_CODE_CHAR_COUNT = 3

export const FLOOR_CEILING_HEIGHT_FEET_MAX = 20
export const FLOOR_CEILING_HEIGHT_FEET_MIN = 5
export const FLOOR_CEILING_HEIGHT_FEET_STEP = 1

export const MAX_INCH_VALUE = 11.75
export const MIN_INCH_VALUE = 0
export const INCH_STEP = 0.25
export const INCHES_IN_FEET = 12
export const MAX_GARAGE_SIZE_SQ_FT_LENGTH = 20

export const ONE_QUARTER = 0.25
export const THREE_QUARTERS = 0.75
export const BEGINNING_OF_ONE_QUARTER = 0.2
export const BEGINNING_OF_THREE_QUARTERS = 0.7
export const ZERO_TO_PREVENT_DOUBLE_COUNTING = 0

export const MAX_FILES_PER_UPLOAD =
  Number(getEnvVar('MAX_FILES_PER_UPLOAD')) || 10

export const BYTES_PER_KILOBYTE = 1024
export const KILOBYTES_PER_MEGABYTE = 1024
export const MIN_ARCH_ENG_PDF_SIZE_MB =
  Number(getEnvVar('REACT_APP_MIN_ARCH_ENG_PDF_SIZE_MB')) || 2
export const MIN_ARCH_ENG_PDF_SIZE_BYTES =
  MIN_ARCH_ENG_PDF_SIZE_MB * KILOBYTES_PER_MEGABYTE * BYTES_PER_KILOBYTE

export const LENNAR_ACCOUNT_ACCESS_ERROR_MESSAGE =
  'Your Lennar account does not have the required access to continue using this application.'
export const LENNAR_SERVICE_DESK_CONTACT_INFO =
  'Please feel free to contact the Lennar Service Desk at 1-800-588-9411 or by email at ServiceDesk@Lennar.com.'

export const BASEMENT_TYPE_UNFINISHED = 'Unfinished'
export const BASEMENT_TYPE_FINISHED = 'Finished'
export const SINGLE_FAMILY_DEFAULT_HIDDEN_COLUMNS =
  'singleFamilyDefaultHiddenColumns_v1.0'
export const MULTI_FAMILY_DEFAULT_HIDDEN_COLUMNS =
  'multiFamilyDefaultHiddenColumns_v1.0'
export const SINGLE_FAMILY_HEADER_FILTERS = 'singleFamilyHeaderFilters'
export const MULTI_FAMILY_HEADER_FILTERS = 'multiFamilyHeaderFilters'
export const USER_COLUMN_WIDTH_SETTING = 'userColumnWidthSetting'
export const SINGLE_FAMILY_GROUPING_MODEL = 'singleFamilyGroupingModel'
export const MULTI_FAMILY_GROUPING_MODEL = 'multiFamilyGroupingModel'
export const SINGLE_FAMILY_SEARCH_CRITERIA = 'singleFamilySearchCriteria'
export const MULTI_FAMILY_SEARCH_CRITERIA = 'multiFamilySearchCriteria'
export const SINGLE_FAMILY_ROWS = 'singleFamilyRowsPerPage'
export const MULTI_FAMILY_ROWS = 'multiFamilyRowsPerPage'
export const SINGLE_FAMILY_SORT_MODEL = 'singleFamilySortModel'
export const MULTI_FAMILY_SORT_MODEL = 'multiFamilySortModel'
export const PENDING_PLANS_ROWS = 'pendingPlansRowsPerPage'
export const PENDING_PLANS_DEFAULT_HIDDEN_COLUMNS =
  'pendingPlansDefaultHiddenColumns'
export const PENDING_PLANS_GROUPING_MODEL = 'pendingPlansGroupingModel'
export const oneThroughNineArray = [1, 2, 3, 4, 5, 6, 7, 8, 9]
export const oneThroughTenArray = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]

export const NEW_PLAN_ID = 'newPlanId'
export const INITIAL_SAVE = 'initialSave'
export const AUTOFILL_DOC = 'autofillDoc'

export const VIEW_FINANCIALS = 'View Financials'
export const MARK_AS_CORE = 'Mark As Core'
export const UNMARK_AS_CORE = 'Unmark As Core'
export const MARK_AS_CORE_AND_SUBMIT = 'Mark As Core and Submit to E1'
export const UNMARK_AS_CORE_AND_SUBMIT = 'Unmark As Core and Submit to E1'

export const VIEW_PLAN_HISTORY = 'View Plan History'
export const VIEW_PLAN_HISTORY_DISABLED =
  'This plan is a draft therefore there is no history.'

export const DEFAULT_DIVISIONS_DATA = {
  divisionCodes: {} as Record<string, string>,
  divisions: {} as Record<string, string>,
}

// LTGC-5759: a plan's lennar.com page link must point at this origin to render
export const LENNAR_COM_ORIGIN = 'https://www.lennar.com'

export const VIEW_ON_LENNAR_COM = 'View on Lennar.com'
